import { call, put, takeLatest, all, select } from "redux-saga/effects";
import {
  LOGIN_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGOUT_REQUEST,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
} from "../../actionTypes/login";
import { realcheckLogin, LoginResponse, doLogout, LogoutResponse } from "../../../com/login";
import { LoginParams, LogoutParams } from "../../action/login";
import { API_CODES } from "../../../constants";
import type { RootState } from "../../../reducers/index";
import { storeAuthData } from "../../../utils/auth/authStorage";

interface LoginRequestAction {
  type: string;
  payload: LoginParams;
}

interface LogoutRequestAction {
  type: string;
  payload: LogoutParams;
}

export function* fetchLogin(
  params: LoginRequestAction,
): Generator<unknown, void, LoginResponse> {
  try {
    const response = yield call(realcheckLogin, params.payload);

    // Check for SUCCESS status (new flat structure: status is string, not nested)
    if (response.status === API_CODES.SUCCESS_CODE || response.status === "SUCCESS") {
      // Case 1: Direct login with tokens (has data field)
      if (response.data && response.data.accessToken) {
        // Store auth data using unified storage method
        // Store in Redux store (persisted to persist:userdb)
        storeAuthData({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          sessionId: response.data.sessionId,
          activeRole: response.data.activeRole,
          availableRoles: response.data.availableRoles,
          user: {
            firstname: "",
            lastname: "",
          },
        });
        
        yield put({
          type: LOGIN_SUCCESS,
          payload: {
            message: response.statusinfo?.message || "Login successful",
            data: response.data,
          },
        });
      } 
      // Case 2: OTP required (SUCCESS but no data - OTP sent message)
      else {
        // Navigate to OTP screen for OTP verification
        yield put({
          type: LOGIN_SUCCESS,
          payload: {
            message: response.statusinfo?.message || "OTP has been sent. Please verify.",
            data: undefined, // No token data yet, will get after OTP verification
          },
        });
      }
    } else {
      // Handle all error cases - 400, 401, 409, 500, etc.
      const errorMessage = response.statusinfo?.message || 
                          "Invalid credentials. Please try again.";
      
      yield put({
        type: LOGIN_FAILURE,
        payload: errorMessage,
      });
    }
  } catch (error) {
    // Handle any unexpected errors
    const errorMessage = error instanceof Error ? error.message : "Please try again later";
    yield put({
      type: LOGIN_FAILURE,
      payload: errorMessage,
    });
  }
}

export function* handleLogout(
  action: LogoutRequestAction,
): Generator<unknown, void, LogoutResponse> {
  try {
    // Get access token from authDetails state for Authorization header
    const accessToken = (yield select(
      (state: RootState) => state.authDetails?.auth?.accessToken
    )) as unknown as string | undefined;
    
    const response = (yield call(doLogout, action.payload, accessToken)) as LogoutResponse;

    if (response.status === API_CODES.SUCCESS_CODE || response.status === "SUCCESS") {
      // Clear auth data from localStorage after successful logout
      yield put({
        type: "RESET_VERIFY_OTP_STATE",
      });
      
      yield put({
        type: LOGOUT_SUCCESS,
        payload: response.data,
      });
    } else {
      yield put({
        type: LOGOUT_FAILURE,
        payload: response.statusinfo.message,
      });
    }
  } catch {
    yield put({
      type: LOGOUT_FAILURE,
      payload: "Logout failed. Please try again.",
    });
  }
}

export function* loginSaga() {
  yield all([
    takeLatest(LOGIN_REQUEST, fetchLogin),
    takeLatest(LOGOUT_REQUEST, handleLogout),
  ]);
}
